// @ts-nocheck
import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { fetchTeacherSubjectsWithStudents, bulkDownloadFamilyReports } from '../../services/api';
import StudentNameOrderToggle from '../../components/StudentNameOrderToggle';
import PageBackNavigation from '../../components/PageBackNavigation';
import { sortStudentsByName, useStudentNameOrder } from '../../utils/studentSort';

export default function BulkReportDownload() {
  const navigate = useNavigate();
  const location = useLocation();
  const { classId } = useParams();

  const [subjects, setSubjects] = useState<any>([]);
  const [loading, setLoading] = useState<any>(true);
  const [error, setError] = useState<any>('');
  const [selected, setSelected] = useState(new Set());
  const [downloading, setDownloading] = useState<any>(false);
  const [downloadMsg, setDownloadMsg] = useState<any>('');
  const [nameOrder] = useStudentNameOrder();

  useEffect(() => {
    setLoading(true);
    fetchTeacherSubjectsWithStudents()
      .then((data) => setSubjects(data.subjects || []))
      .catch(() => setError('Could not load students.'))
      .finally(() => setLoading(false));
  }, []);

  // Find the specific class across all subjects
  const classInfo = useMemo(() => {
    for (const subject of subjects) {
      const cls = (subject.classes || []).find((c) => String(c.class_id) === String(classId));
      if (cls) return { cls, subject };
    }
    return null;
  }, [subjects, classId]);

  const students = useMemo(() => {
    if (!classInfo) return [];
    return sortStudentsByName(classInfo.cls.students, nameOrder);
  }, [classInfo, nameOrder]);

  const allSelected = students.length > 0 && selected.size === students.length;

  const toggleAll = () => {
    if (allSelected) {
      setSelected(new Set());
    } else {
      setSelected(new Set(students.map((s) => s.student_id)));
    }
  };

  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleDownload = async () => {
    if (selected.size === 0) {
      setDownloadMsg('Please select at least one student.');
      return;
    }
    setDownloading(true);
    setDownloadMsg('');
    try {
      const blob = await bulkDownloadFamilyReports([...selected], Number(classId));
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      const className = classInfo?.cls?.class_name || `Class_${classId}`;
      a.href = url;
      a.download = `${className.replace(/\s+/g, '_')}_family_reports.zip`;
      a.click();
      URL.revokeObjectURL(url);
      setDownloadMsg(`Downloaded reports for ${selected.size} student${selected.size !== 1 ? 's' : ''}.`);
    } catch (_err: any) {
      setDownloadMsg('Download failed. Please try again.');
    } finally {
      setDownloading(false);
    }
  };

  const goBack = () => {
    const homeState = location.state?.homeState;
    navigate('/teacher/home', { state: homeState ? { homeState } : undefined });
  };

  if (loading) return <div style={{ padding: 24, color: '#6b7280', textAlign: 'center' }}>Loading students...</div>;

  if (error) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '16px 20px', color: '#b91c1c', fontWeight: 600 }}>{error}</div>
    </div>
  );

  if (!classInfo) return (
    <div style={{ padding: 24 }}>
      <div style={{ background: '#fef2f2', border: '1.5px solid #fca5a5', borderRadius: 12, padding: '16px 20px', color: '#b91c1c', fontWeight: 600 }}>Class not found.</div>
    </div>
  );

  const { cls, subject } = classInfo;

  return (
    <div>
      <PageBackNavigation onBack={goBack} label="Back to Home" />

      <h1 style={{ fontSize: 24, fontWeight: 800, color: '#1e3a5f', marginBottom: 4 }}>
        Bulk Download Family Reports
      </h1>
      <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
        {cls.class_name || `Class ${cls.class_id}`} &nbsp;·&nbsp; {subject.subject_name}
      </div>
      <p style={{ fontSize: 14, color: '#6b7280', marginBottom: 20 }}>
        Each PDF shows the student's profile snapshot across all subjects, with grade-level progress colors from Pre-K through grade 12.
      </p>

      <div style={{ background: '#ffffff', border: '1.5px solid #e0e7ef', borderRadius: 14, padding: '20px 24px', maxWidth: 620 }}>
        {/* Select all row */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 14, paddingBottom: 12, borderBottom: '1px solid #e5e7eb' }}>
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
            <input
              type="checkbox"
              checked={allSelected}
              onChange={toggleAll}
              style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }}
            />
            Select All ({students.length} students)
          </label>
          <StudentNameOrderToggle />
          <span style={{ fontSize: 13, color: '#6b7280' }}>{selected.size} selected</span>
        </div>

        {/* Student list */}
        {students.length === 0 ? (
          <div style={{ color: '#6b7280', fontSize: 13 }}>No students in this class.</div>
        ) : (
          <div style={{ maxHeight: 380, overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {students.map((student) => {
              const name = `${student.first_name || ''} ${student.last_name || ''}`.trim() || `Student ${student.student_id}`;
              const isChecked = selected.has(student.student_id);
              return (
                <label
                  key={student.student_id}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 12,
                    padding: '10px 12px',
                    borderRadius: 10,
                    border: isChecked ? '1px solid #16a34a' : '1px solid #e5e7eb',
                    background: isChecked ? '#f0fdf4' : '#f9fafb',
                    cursor: 'pointer',
                    transition: 'all 0.15s',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={isChecked}
                    onChange={() => toggleOne(student.student_id)}
                    style={{ width: 16, height: 16, cursor: 'pointer', accentColor: '#16a34a' }}
                  />
                  <div>
                    <div style={{ fontWeight: 600, fontSize: 14, color: '#1e3a5f' }}>{name}</div>
                    <div style={{ fontSize: 12, color: '#6b7280' }}>
                      ID: {student.student_id} &nbsp;·&nbsp; Grade: {student.grade_level ?? 'N/A'}
                    </div>
                  </div>
                </label>
              );
            })}
          </div>
        )}

        {/* Actions */}
        <div style={{ marginTop: 18, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
          <button
            type="button"
            disabled={downloading || selected.size === 0}
            onClick={handleDownload}
            style={{
              background: selected.size === 0 ? '#9ca3af' : '#1e3a8a',
              color: '#fff',
              border: 'none',
              borderRadius: 10,
              padding: '10px 22px',
              fontWeight: 700,
              fontSize: 14,
              cursor: downloading || selected.size === 0 ? 'default' : 'pointer',
              opacity: downloading ? 0.75 : 1,
            }}
          >
            {downloading ? 'Generating ZIP...' : `📥 Download ${selected.size > 0 ? selected.size : ''} Report${selected.size !== 1 ? 's' : ''}`}
          </button>
          {downloadMsg && (
            <span style={{ fontSize: 13, fontWeight: 600, color: downloadMsg.includes('failed') || downloadMsg.includes('select') ? '#b91c1c' : '#166534' }}>
              {downloadMsg}
            </span>
          )}
        </div>
      </div>
      <PageBackNavigation onBack={goBack} label="Back to Home" position="bottom" />
    </div>
  );
}
